import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  Tooltip,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  styled,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Chip,
  Fade,
  Grow,
  LinearProgress,
  Avatar,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolIcon from "@mui/icons-material/School";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import GradeIcon from "@mui/icons-material/WorkspacePremium";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import base64 from "base64-js";
import { useParams } from "react-router-dom";

/* ============================================================================
   DESIGN TOKENS
   Kept consistent with the rest of the exam/academics module: deep navy →
   indigo gradient header, Sora for display type, Inter for body/UI.
   ============================================================================ */
const T = {
  ink: "#0F1923",
  slate: "#1E2D3D",
  indigo: "#2563EB",
  indigoD: "#1D4ED8",
  teal: "#0D9488",
  emerald: "#059669",
  amber: "#D97706",
  rose: "#E11D48",
  violet: "#7C3AED",
  sky: "#0284C7",
  skyL: "#E0F2FE",
  border: "#E2E8F0",
  surface: "#F3F5FA",
  card: "#FFFFFF",
  muted: "#64748B",
  faint: "#94A3B8",
  ghost: "#F8FAFC",
};
const FONT_DISPLAY = "'Sora', 'Segoe UI', sans-serif";
const FONT_BODY = "'Inter', 'Segoe UI', sans-serif";

/* ---------- inject webfonts once ---------- */
function useWebFonts() {
  useEffect(() => {
    if (document.getElementById("add-exam-fonts")) return;
    const link = document.createElement("link");
    link.id = "add-exam-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

// Grade scale used for pre-primary classes (Nursery / LKG / UKG).
// The user picks a grade; the numeric value shown here is what actually
// travels in the obtainedMarks field of the save payload.
//   >= 90  A+
//   >= 80  A
//   >= 70  B+
//   >= 60  B
//   >= 50  C
//   else   F   (placeholder value below — confirm the exact number to store)

const GRADE_SCALE = [
  { grade: "A+", percent: 90, color: T.emerald },
  { grade: "A", percent: 80, color: "#16A34A" },
  { grade: "B+", percent: 70, color: T.sky },
  { grade: "B", percent: 60, color: "#0EA5E9" },
  { grade: "C", percent: 50, color: T.amber },
  { grade: "F", percent: 40, color: T.rose },
];

// Classes that use grades instead of raw numeric marks.
const GRADE_CLASS_NAMES = ["nur", "lkg", "ukg", "nursery"];

// Converts a grade's percentage threshold into an actual mark value
// scaled to that student's real maxMarks (falls back to 100 if missing/invalid).
const gradeValueForMax = (percent, maxMarks) => {
  const max = Number(maxMarks);
  const safeMax = Number.isFinite(max) && max > 0 ? max : 100;
  return Math.round((percent / 100) * safeMax);
};

const isGradeClassName = (className) =>
  GRADE_CLASS_NAMES.includes(String(className || "").trim().toLowerCase());

const StyledButton = styled(Button)(() => ({
  borderRadius: 12,
  padding: "9px 24px",
  textTransform: "none",
  fontWeight: 700,
  transition: "all 0.25s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
  },
}));

const fieldRadius = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: T.ghost,
    transition: "all .2s",
    "&:hover fieldset": { borderColor: T.indigo },
    "&.Mui-focused fieldset": { borderColor: T.indigo, borderWidth: 1.5 },
  },
};

const thSx = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: T.slate,
  py: 1.4,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  fontFamily: FONT_BODY,
  borderBottom: `2px solid ${T.border}`,
};

// Small info-guide trigger button used in the header.
const IconButtonLike = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 38,
      height: 38,
      borderRadius: "10px",
      bgcolor: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.16)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all .2s",
      "&:hover": { bgcolor: "rgba(255,255,255,0.16)", transform: "translateY(-2px)" },
    }}
  >
    <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
  </Box>
);

// Small header KPI pill.
const HeaderChip = ({ icon, label, value }) => (
  <Box
    sx={{
      bgcolor: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.16)",
      borderRadius: "12px",
      px: 1.6,
      py: 0.8,
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 110,
    }}
  >
    {icon}
    <Box>
      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1rem", lineHeight: 1, fontFamily: FONT_DISPLAY }}>
        {value}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.62rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const AddExam = () => {
  const [canSave, setCanSave] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [examTypes, setExamTypes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [subExams, setSubExams] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [subjectOption, setSubjectOption] = useState("withSubject");
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubExam, setSelectedSubExam] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [isWithSubject, setIsWithSubject] = useState(true);
  const [studentList, setStudentList] = useState([]);
  const [showSaveCancel, setShowSaveCancel] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isExamModeIndependent, setIsExamModeIndependent] = useState(0);
  const [isSubExamDisabled, setIsSubExamDisabled] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useWebFonts();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(
        Math.ceil(encodedFormId.length / 4) * 4,
        "="
      );
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });
      if (response.ok) {
        const responseData = await response.json();
        const authorizationData = responseData[0];
        setCanSave(authorizationData.uWrite === 1);
      } else {
        console.error("Authorization failed:", response.status);
        alert("Invalid authorization response");
      }
    } catch (error) {
      console.error("API request error:", error);
      alert("An error occurred. Please try again later.");
    }
  };

  const handleSubjectOptionChange = (event) => {
    setSubjectOption(event.target.value);
    setIsWithSubject(event.target.value === "withSubject");
    setSelectedSubject("");
  };

  useEffect(() => {
    if (subjectOption === "withoutSubject") {
      setSelectedSubject("");
    }
  }, [subjectOption]);

  const fetchSessions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/ClassPromotion/GetFinancialYear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setSessions(data);

        const today = new Date();
        const currentYear = today.getFullYear();
        const nextYear = currentYear + 1;

        const financialYearStart = new Date(currentYear, 3, 1);
        const financialYearEnd = new Date(nextYear, 2, 31);

        let currentSessionString;
        if (today >= financialYearStart && today <= financialYearEnd) {
          currentSessionString = `${currentYear}-${nextYear}`;
        } else {
          currentSessionString = `${currentYear - 1}-${currentYear}`;
        }

        const currentSession = data.find(
          (item) => item.finanacialYear === currentSessionString
        );

        if (currentSession) {
          setSession(currentSession.financialYearID);
        }
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
      setSnackbarMessage("Failed to fetch sessions.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/ExaminationCriteria/GetExaminationCriteria`;
        const token = sessionStorage.getItem("token");

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: 0 }),
        });

        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          setExamTypes(data);
        } else {
          setSnackbarMessage("No exam types found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching exam types:", error);
        setSnackbarMessage("Failed to fetch exam types. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };

    fetchExamTypes();
    Authorizer();
  }, []);

  useEffect(() => {
    if (!selectedExamType) return;

    const selectedExam = examTypes.find(
      (exam) => exam.examType === selectedExamType
    );

    if (selectedExam) {
      setIsExamModeIndependent(selectedExam.isExamModeIndependent);

      if (selectedExam.isExamModeIndependent === 1) {
        setSnackbarMessage(
          "Sub-exam selection is disabled as the exam mode is independent."
        );
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
        setIsSubExamDisabled(true);
      } else {
        setIsSubExamDisabled(false);
      }
    }
  }, [selectedExamType, examTypes]);

  useEffect(() => {
    if (!selectedExamType || isExamModeIndependent === 1) return;

    const fetchSubExams = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/Teacher/ddlExamSubCategory_Examination`;
        const token = sessionStorage.getItem("token");

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: selectedExamType }),
        });

        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          setSubExams(
            data.map((subExam) => ({
              id: subExam.examSubCategoryId,
              name: subExam.examSubCategory,
            }))
          );
          setIsSubExamDisabled(false);
        } else {
          setSubExams([]);
          setSnackbarMessage("No sub-exams found for the selected exam type.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          setIsSubExamDisabled(true);
        }
      } catch (error) {
        console.error("Error fetching sub-exams:", error);
        setSnackbarMessage("Failed to fetch sub-exams. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setIsSubExamDisabled(true);
      }
    };

    fetchSubExams();
  }, [selectedExamType, isExamModeIndependent]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // ---- Class / Section / Subject master data -------------------------------
  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassData(Array.isArray(responseData) ? responseData : []);
        if (!Array.isArray(responseData) || responseData.length === 0) {
          setSnackbarMessage("No classes found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } else {
        console.error("Failed to fetch class data");
        setSnackbarMessage("Failed to fetch classes.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("Error fetching classes. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchSections = async (classId) => {
    if (!classId) {
      setSectionData([]);
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/ClassPromotion/ddlSection_clsId`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId: 0, classId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setSectionData(Array.isArray(responseData) ? responseData : []);
      } else {
        console.error("Failed to fetch section data");
        setSnackbarMessage("Failed to fetch sections.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setSectionData([]);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("Error fetching sections. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setSectionData([]);
    }
  };

  const fetchSubjects = async (classId) => {
    if (!classId) {
      setSubjectData([]);
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/ClassMapping/GetSubjectMapping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ classId }),
      });

      if (response.ok) {
        const responseData = await response.json();

        let subjects = [];
        if (Array.isArray(responseData)) {
          subjects = responseData;
        } else if (responseData && Array.isArray(responseData.data)) {
          subjects = responseData.data;
        }
        // Only active subjects (status === "1") should be selectable.
        subjects = subjects.filter((s) => s.status === "1");

        setSubjectData(subjects);

        if (subjects.length === 0) {
          setSnackbarMessage("No subjects found for the selected class.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } else {
        console.error("Failed to fetch subject data");
        setSnackbarMessage("Failed to fetch subjects.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setSubjectData([]);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("Error fetching subjects. Please try again.");
      setSnackbarSeverity("error");
      setSubjectData([]);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchSessions();
  }, []);

  useEffect(() => {
    setSelectedSection("");
    setSelectedSubject("");
    if (selectedClass !== "") {
      fetchSections(selectedClass);
      fetchSubjects(selectedClass);
    } else {
      setSectionData([]);
      setSubjectData([]);
    }
  }, [selectedClass]);

  // Whether the currently selected class is a grade-based (pre-primary) class.
  const isGradeClass = useMemo(() => {
    const cls = classData.find((c) => c.classId === selectedClass);
    return isGradeClassName(cls?.className);
  }, [classData, selectedClass]);

 const gradeForValue = (value, maxMarks) => {
  if (value === "" || value === null || value === undefined) return "";
  const match = GRADE_SCALE.find(
    (g) => gradeValueForMax(g.percent, maxMarks) === Number(value)
  );
  return match ? match.grade : "";
};

  // All the filters required before a student list can be fetched.
  const requiredFiltersReady = useMemo(
    () =>
      Boolean(
        selectedExamType &&
          (isSubExamDisabled || selectedSubExam) &&
          selectedClass &&
          selectedSection &&
          (subjectOption !== "withSubject" || selectedSubject)
      ),
    [
      selectedExamType,
      isSubExamDisabled,
      selectedSubExam,
      selectedClass,
      selectedSection,
      subjectOption,
      selectedSubject,
    ]
  );

  // Pure fetch — no validation here, callers decide when it's appropriate to call.
  const fetchStudentList = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/Exam/GetStudentList_Exam`;
      const token = sessionStorage.getItem("token");

      const selectedExam = examTypes.find(
        (exam) => exam.examCriteriaId === selectedExamType
      );
      const isExamModeIndependentValue = selectedExam
        ? selectedExam.isExamModeIndependent === 1
        : false;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          classId: selectedClass,
          sectionId: selectedSection,
          subjectId: selectedSubject || 0,
          examTypeId: selectedExamType,
          subCategoryId: selectedSubExam || 0,
          isWithSubject: isWithSubject,
          isExamModeIndependent: isExamModeIndependentValue,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const list = Array.isArray(responseData) ? responseData : [];
        setStudentList(list);
        setSelectAll(false);
        setShowSaveCancel(list.length > 0);
        if (list.length === 0) {
          setSnackbarMessage("No students found for the selected criteria.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } else {
        console.error("Failed to get student list");
        setSnackbarMessage("Failed to fetch student list.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setShowSaveCancel(false);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("Error fetching student list. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setShowSaveCancel(false);
    } finally {
      setStudentsLoading(false);
    }
  }, [
    examTypes,
    selectedExamType,
    selectedClass,
    selectedSection,
    selectedSubject,
    selectedSubExam,
    isWithSubject,
  ]);

  // Auto-fetch: whenever the filters change and are all filled in, re-run the
  // query and re-render the table. Clears the table if filters become incomplete.
  useEffect(() => {
    if (requiredFiltersReady) {
      fetchStudentList();
    } else {
      setStudentList([]);
      setShowSaveCancel(false);
      setSelectAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredFiltersReady, selectedExamType, selectedSubExam, selectedClass, selectedSection, selectedSubject, subjectOption]);

  const handleReloadClick = () => {
    if (!requiredFiltersReady) {
      setOpenDialog(true);
      return;
    }
    fetchStudentList();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const clearForm = () => {
    setSelectedExamType("");
    setSelectedSubExam("");
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setSubjectOption("withSubject");
    setStudentList([]);
    setShowSaveCancel(false);
    setSelectAll(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/Exam/SaveStudentMarks`;
      const token = sessionStorage.getItem("token");

      const selectedStudents = studentList.filter((student) => student.checked);

      if (selectedStudents.length === 0) {
        setSnackbarMessage("Please select at least one student to save marks.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setSaving(false);
        return;
      }

      const requestBody = selectedStudents.map((student) => ({
        examTypeId: selectedExamType,
        studentId: student.studentId,
        classId: selectedClass,
        sectionId: selectedSection,
        subjectId: selectedSubject || 0,
        maxMarks: student.maxMarks,
        minMarks: student.minMarks,
        obtainedMarks: student.obtainedMarks,
        subExamCategoryId: selectedSubExam || 0,
        isSubject: isWithSubject,
        isAttendedExam: true,
        sessionId: session,
      }));

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // Keep the table visible after saving — just mark the saved rows as
        // unchecked again so the teacher can review/continue without losing data.
        setStudentList((prevList) =>
          prevList.map((s) => (s.checked ? { ...s, checked: false } : s))
        );
        setSelectAll(false);
        setSnackbarMessage("Data saved successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("Failed to save data.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage("Error saving data. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSessionChange = (event) => {
    setSession(event.target.value);
  };

  const handleCheckboxChange = (index, checked) => {
    setStudentList((prevList) =>
      prevList.map((student, idx) =>
        idx === index ? { ...student, checked } : student
      )
    );
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    setStudentList((prevList) =>
      prevList.map((student) => ({ ...student, checked: isChecked }))
    );
  };

  const selectedCount = useMemo(
    () => studentList.filter((s) => s.checked).length,
    [studentList]
  );

  return (
    <Box sx={{ bgcolor: T.surface, minHeight: "100vh", pb: 6, fontFamily: FONT_BODY }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${T.ink} 0%, #1A3460 55%, #1e4d8c 100%)`,
          py: 3.5,
          px: { xs: 2, md: 4 },
          mb: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -60,
            right: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(37,99,235,0.25) 0%, rgba(37,99,235,0) 70%)",
            animation: "floatA 9s ease-in-out infinite",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -50,
            left: "22%",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(124,58,237,0.16) 0%, rgba(124,58,237,0) 70%)",
            animation: "floatB 11s ease-in-out infinite",
          },
          "@keyframes floatA": {
            "0%,100%": { transform: "translate(0,0)" },
            "50%": { transform: "translate(-16px,14px)" },
          },
          "@keyframes floatB": {
            "0%,100%": { transform: "translate(0,0)" },
            "50%": { transform: "translate(14px,-10px)" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: T.indigo,
                borderRadius: "12px",
                p: 1.1,
                display: "flex",
                boxShadow: "0 4px 14px rgba(37,99,235,0.45)",
              }}
            >
              <SchoolIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  letterSpacing: -0.5,
                  fontSize: "1.5rem",
                  fontFamily: FONT_DISPLAY,
                }}
              >
                Enter Exam Marks
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}
              >
                Pick your criteria — the student list updates automatically
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HeaderChip
              icon={<GroupsIcon sx={{ color: "#93C5FD", fontSize: 20 }} />}
              label="Students"
              value={studentList.length}
            />
            <HeaderChip
              icon={<CheckCircleIcon sx={{ color: "#6EE7B7", fontSize: 20 }} />}
              label="Selected"
              value={selectedCount}
            />
            <Tooltip title="View user guide">
              <IconButtonLike onClick={() => setShowUserGuide(true)} />
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* User guide dialog */}
      <Dialog
        open={showUserGuide}
        onClose={() => setShowUserGuide(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            backgroundColor: "#f9fafb",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(90deg, ${T.indigo} 0%, ${T.violet} 100%)`,
            color: "white",
            fontWeight: 800,
            textAlign: "center",
            borderRadius: "16px 16px 0 0",
            py: 2,
            fontFamily: FONT_DISPLAY,
          }}
        >
          Exam User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography
            variant="h6"
            sx={{
              color: T.indigo,
              fontWeight: 700,
              mb: 2,
              mt: 1,
              textAlign: "center",
              fontFamily: FONT_DISPLAY,
            }}
          >
            Quick Steps to Take Exam
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyleType: "none",
              padding: 0,
              "& li": {
                display: "flex",
                alignItems: "flex-start",
                mb: 1.5,
                fontSize: "0.95rem",
                color: "#333",
              },
              "& li::before": {
                content: '"\\2022"',
                color: T.indigo,
                fontWeight: "bold",
                display: "inline-block",
                width: "1.5em",
                mr: 1,
                fontSize: "1.2rem",
              },
              "& strong": { margin: "0 0.2em" },
            }}
          >
            <li>Select a <strong>Session</strong> from the dropdown menu.</li>
            <li>Choose whether the exam is <strong>With Subject</strong> or <strong>Without Subject</strong> using the radio buttons.</li>
            <li>Select an <strong>Exam Type</strong> from the dropdown menu.</li>
            <li>If applicable, select a <strong>Sub Exam</strong> from the dropdown menu.</li>
            <li>Select a <strong>Class</strong> from the dropdown menu.</li>
            <li>Select a <strong>Section</strong> from the dropdown menu.</li>
            <li>If you chose "With Subject", select a <strong>Subject</strong> from the dropdown menu.</li>
            <li>The student list loads automatically once all required filters are set — use <strong>Reload</strong> to refresh it manually.</li>
            <li>Check the checkboxes to select students, then enter <strong>Obtained Marks</strong> — or pick a <strong>Grade</strong> for Nursery / LKG / UKG classes.</li>
            <li>Click the <strong>Save</strong> button to save the entered marks. The table stays on screen afterwards.</li>
            <li>To clear the form and start over, click the <strong>Cancel</strong> button.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <StyledButton
            onClick={() => setShowUserGuide(false)}
            variant="contained"
            sx={{
              backgroundColor: T.indigo,
              "&:hover": { backgroundColor: T.indigoD },
              px: 4,
            }}
          >
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Container maxWidth={false} sx={{ px: { xs: 1.5, md: 4 } }}>
        <Fade in timeout={450}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "18px",
              boxShadow: "0 4px 24px rgba(15,25,35,0.08)",
              border: `1px solid ${T.border}`,
              p: { xs: 2, md: 3 },
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="session-label">Session</InputLabel>
                  <Select
                    labelId="session-label"
                    value={session}
                    onChange={handleSessionChange}
                    label="Session"
                    sx={{ ...fieldRadius["& .MuiOutlinedInput-root"], height: 44 }}
                  >
                    <MenuItem value="" disabled>
                      Select Session
                    </MenuItem>
                    {sessions.map((item) => (
                      <MenuItem
                        key={item.financialYearID}
                        value={item.financialYearID}
                      >
                        {item.finanacialYear}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={9}>
                <RadioGroup
                  row
                  aria-label="subjectOption"
                  name="subjectOption"
                  value={subjectOption}
                  onChange={handleSubjectOptionChange}
                >
                  <FormControlLabel
                    value="withSubject"
                    control={<Radio sx={{ color: T.indigo, "&.Mui-checked": { color: T.indigo } }} />}
                    label="With Subject"
                  />
                </RadioGroup>
              </Grid> */}
            </Grid>

            <Box sx={{ my: 2.5, borderTop: `1px solid ${T.border}` }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="exam-type-label">Exam Type</InputLabel>
                  <Select
                    labelId="exam-type-label"
                    id="exam-type"
                    value={selectedExamType}
                    onChange={(e) => {
                      setSelectedExamType(e.target.value);
                      setSelectedSubExam("");
                    }}
                    label="Exam Type"
                    sx={fieldRadius["& .MuiOutlinedInput-root"]}
                  >
                    <MenuItem value="" disabled>
                      Select Exam Type
                    </MenuItem>
                    {examTypes.map((type) => (
                      <MenuItem key={type.examCriteriaId} value={type.examCriteriaId}>
                        {type.examType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" disabled={isSubExamDisabled}>
                  <InputLabel id="sub-exam-label">Sub Exam</InputLabel>
                  <Select
                    labelId="sub-exam-label"
                    id="sub-exam"
                    value={selectedSubExam}
                    onChange={(e) => setSelectedSubExam(e.target.value)}
                    label="Sub Exam"
                    sx={fieldRadius["& .MuiOutlinedInput-root"]}
                  >
                    <MenuItem value="" disabled>
                      Select Sub Exam
                    </MenuItem>
                    {subExams.map((subExam) => (
                      <MenuItem key={subExam.id} value={subExam.id}>
                        {subExam.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="class-label">Class</InputLabel>
                  <Select
                    labelId="class-label"
                    id="class"
                    label="Class"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    sx={fieldRadius["& .MuiOutlinedInput-root"]}
                  >
                    <MenuItem value="" disabled>
                      Select Class
                    </MenuItem>
                    {classData.map((classItem) => (
                      <MenuItem key={classItem.classId} value={classItem.classId}>
                        {classItem.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" disabled={!selectedClass}>
                  <InputLabel id="section-label">Section</InputLabel>
                  <Select
                    labelId="section-label"
                    id="section"
                    label="Section"
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    sx={fieldRadius["& .MuiOutlinedInput-root"]}
                  >
                    <MenuItem value="" disabled>
                      Select Section
                    </MenuItem>
                    {sectionData.map((sectionItem) => (
                      <MenuItem
                        key={sectionItem.sectionId}
                        value={sectionItem.sectionId}
                      >
                        {sectionItem.sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {subjectOption === "withSubject" && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" disabled={!selectedClass}>
                    <InputLabel id="subject-label">Subject</InputLabel>
                    <Select
                      labelId="subject-label"
                      id="subject"
                      label="Subject"
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      sx={fieldRadius["& .MuiOutlinedInput-root"]}
                    >
                      <MenuItem value="" disabled>
                        Select Subject
                      </MenuItem>
                      {Array.isArray(subjectData) &&
                        subjectData.map((subjectItem) => (
                          <MenuItem
                            key={subjectItem.subjectId}
                            value={subjectItem.subjectId}
                          >
                            {subjectItem.subjectName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <StyledButton
                  fullWidth
                  variant="outlined"
                  onClick={handleReloadClick}
                  startIcon={
                    studentsLoading ? (
                      <CircularProgress size={16} sx={{ color: T.indigo }} />
                    ) : (
                      <ReplayIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  disabled={studentsLoading}
                  sx={{
                    height: 44,
                    borderColor: T.border,
                    color: T.slate,
                    "&:hover": { borderColor: T.indigo, color: T.indigo, bgcolor: "#EFF6FF" },
                  }}
                >
                  {studentsLoading ? "Loading…" : "Reload"}
                </StyledButton>
              </Grid>
            </Grid>

            {isGradeClass && selectedClass && (
              <Grow in>
                <Chip
                  icon={<GradeIcon sx={{ fontSize: 16, color: `${T.violet} !important` }} />}
                  label="This class uses grade-based marking (A+ to F)"
                  sx={{
                    mt: 2,
                    bgcolor: "#F3E8FF",
                    color: T.violet,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    border: "1px solid #E9D5FF",
                  }}
                />
              </Grow>
            )}

            {studentsLoading && (
              <LinearProgress
                sx={{
                  mt: 2,
                  borderRadius: 4,
                  height: 5,
                  "& .MuiLinearProgress-bar": { bgcolor: T.indigo },
                  bgcolor: T.border,
                }}
              />
            )}

            {studentList.length > 0 ? (
              <Fade in timeout={400}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    mt: 3,
                    borderRadius: "14px",
                    border: `1px solid ${T.border}`,
                    overflow: "auto",
                    maxHeight: 620,
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAll}
                            sx={{ color: T.indigo, "&.Mui-checked": { color: T.indigo } }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Student Name</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Roll No</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Class</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Section</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Subject</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Max Marks</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>Min Marks</TableCell>
                        <TableCell sx={{ ...thSx, bgcolor: "#EEF2F9" }}>
                          {isGradeClass ? "Grade" : "Obtained Marks"}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentList.map((student, index) => (
                        <Grow in timeout={250 + index * 40} key={index}>
                          <TableRow
                            hover
                            sx={{
                              "&:last-child td": { border: 0 },
                              "&:hover": { bgcolor: "#F8FAFF" },
                              transition: "background .15s",
                            }}
                          >
                            <TableCell>
                              <Checkbox
                                checked={student.checked || false}
                                onChange={(event) =>
                                  handleCheckboxChange(index, event.target.checked)
                                }
                                sx={{ color: T.indigo, "&.Mui-checked": { color: T.indigo } }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    bgcolor: "#DBEAFE",
                                    color: T.indigo,
                                    fontSize: "0.7rem",
                                    fontWeight: 800,
                                  }}
                                >
                                  {(student.studentName ?? "?").charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                                  {student.studentName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{student.rollNo}</TableCell>
                            <TableCell>{student.className}</TableCell>
                            <TableCell>{student.section}</TableCell>
                            <TableCell>{student.subjectName}</TableCell>
                            <TableCell>{student.maxMarks}</TableCell>
                            <TableCell>{student.minMarks}</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                            {isGradeClass ? (
  <FormControl size="small" fullWidth>
    <Select
      displayEmpty
      value={
        student.obtainedMarks !== undefined &&
        student.obtainedMarks !== "" &&
        gradeForValue(student.obtainedMarks, student.maxMarks)   // pass maxMarks
          ? student.obtainedMarks
          : ""
      }
      onChange={(event) => {
        const value = event.target.value;
        setStudentList((prevList) =>
          prevList.map((prevStudent, idx) =>
            idx === index
              ? { ...prevStudent, obtainedMarks: value }
              : prevStudent
          )
        );
      }}
      sx={{ borderRadius: "10px", fontSize: "0.82rem", bgcolor: T.ghost }}
    >
      <MenuItem value="" disabled>
        Select Grade
      </MenuItem>
      {GRADE_SCALE.map((g) => {
        const scaledValue = gradeValueForMax(g.percent, student.maxMarks); // per-row scaling
        return (
          <MenuItem key={g.grade} value={scaledValue}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: g.color }} />
              {g.grade}
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  </FormControl>
) : (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={student.obtainedMarks || ""}
                                  onChange={(event) => {
                                    let newValue = event.target.value;
                                    if (
                                      newValue === "" ||
                                      (/^\d+$/.test(newValue) &&
                                        parseInt(newValue) >= 0 &&
                                        parseInt(newValue) <= student.maxMarks)
                                    ) {
                                      setStudentList((prevList) =>
                                        prevList.map((prevStudent, idx) =>
                                          idx === index
                                            ? { ...prevStudent, obtainedMarks: newValue }
                                            : prevStudent
                                        )
                                      );
                                    }
                                  }}
                                  inputProps={{
                                    min: 0,
                                    max: student.maxMarks,
                                    step: 1,
                                  }}
                                  onKeyDown={(e) => {
                                    if (["-", "e", "+", "."].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  sx={fieldRadius}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        </Grow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Fade>
            ) : (
              !studentsLoading &&
              requiredFiltersReady === false && (
                <Box
                  sx={{
                    mt: 3,
                    py: 6,
                    textAlign: "center",
                    borderRadius: "14px",
                    border: `1.5px dashed ${T.border}`,
                    bgcolor: T.ghost,
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 36, color: T.faint, mb: 1 }} />
                  <Typography sx={{ color: T.muted, fontSize: "0.85rem" }}>
                    Select exam type, class, section{subjectOption === "withSubject" ? " and subject" : ""} to load students.
                  </Typography>
                </Box>
              )
            )}

            {showSaveCancel && (
              <Box sx={{ textAlign: "center", mt: 3, display: "flex", gap: 1.5, justifyContent: "center" }}>
                <Tooltip title={canSave ? "Save Data" : "Not authorized to save"} arrow>
                  <span>
                    <StyledButton
                      variant="contained"
                      onClick={handleSave}
                      disabled={!canSave || saving}
                      startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        bgcolor: T.emerald,
                        boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
                        "&:hover": { bgcolor: "#047857" },
                        "&:disabled": { opacity: 0.6 },
                      }}
                    >
                      {saving ? "Saving…" : "Save"}
                    </StyledButton>
                  </span>
                </Tooltip>
                <StyledButton
                  variant="outlined"
                  onClick={clearForm}
                  startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderColor: T.border,
                    color: T.slate,
                    "&:hover": { borderColor: T.rose, color: T.rose, bgcolor: "#FFF1F2" },
                  }}
                >
                  Cancel
                </StyledButton>
              </Box>
            )}

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
                variant="filled"
                sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>

            <Dialog
              open={openDialog}
              onClose={handleDialogClose}
              TransitionComponent={Fade}
              PaperProps={{ sx: { borderRadius: "16px" } }}
            >
              <DialogTitle sx={{ fontWeight: 700, fontFamily: FONT_DISPLAY }}>
                Missing Information
              </DialogTitle>
              <DialogContent>
                Please select all required details before proceeding.
              </DialogContent>
              <DialogActions>
                <StyledButton
                  onClick={handleDialogClose}
                  variant="contained"
                  sx={{ bgcolor: T.indigo, "&:hover": { bgcolor: T.indigoD } }}
                >
                  OK
                </StyledButton>
              </DialogActions>
            </Dialog>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default AddExam;